import BaseContainer from '@scenes/base/BaseContainer'
import { Interactive, Button, NineSlice } from '@components/components'


/**
 * NPCDialog - Dialog prompt for NPC interactions
 * 
 * Displays NPC dialog text with response options that players can click.
 * Supports branching dialog trees and quest integration.
 */
export default class NPCDialog extends BaseContainer {

    constructor(scene, x, y) {
        super(scene, x ?? 760, y ?? 480)

        this.visible = false

        // Current NPC data
        this.npcId = null
        this.npcName = null
        this.currentDialog = null

        // Create UI elements
        this.createUI()
    }

    createUI() {
        // Semi-transparent background blocker
        this.block = this.scene.add.rectangle(0, 0, 1520, 960)
        this.block.isFilled = true
        this.block.fillColor = 0x000000
        this.block.fillAlpha = 0.2
        this.add(this.block)

        // Make block interactive to prevent clicks through
        new Interactive(this.block)

        // Main dialog box using CP sprite - wider and taller
        this.dialogBox = this.scene.add.image(0, 0, 'prompt', 'window')
        this.dialogBox.setScale(1.7, 1.5) // Wider prompt
        this.add(this.dialogBox)

        // NPC name header - positioned at the top of the dialog
        this.nameText = this.scene.add.text(0, -175, 'NPC Name', {
            fontFamily: 'Arial Narrow',
            fontSize: '32px',
            fontStyle: 'bold',
            color: '#ffffff',
            align: 'center',
            stroke: '#000000',
            strokeThickness: 4
        })
        this.nameText.setOrigin(0.5, 0.5)
        this.add(this.nameText)

        // Dialog text area - tighter line spacing
        this.dialogText = this.scene.add.text(0, -60, '', {
            fontFamily: 'Arial Narrow',
            fontSize: '24px',
            color: '#ffffff',
            align: 'center',
            fixedWidth: 580,
            wordWrap: { width: 580 },
            lineSpacing: 2
        })
        this.dialogText.setOrigin(0.5, 0.5)
        this.add(this.dialogText)

        // Response buttons container - positioned lower
        this.responsesContainer = this.scene.add.container(0, 80)
        this.add(this.responsesContainer)

        // Close button (X) - positioned in top right corner of dialog box frame
        this.closeButton = this.scene.add.image(260, -195, 'main', 'blue-button')
        this.closeButton.setScale(0.7)
        this.add(this.closeButton)

        this.closeX = this.scene.add.image(260, -197, 'main', 'blue-x')
        this.closeX.setScale(0.7)
        this.add(this.closeX)

        // Make close button interactive
        this.closeButton.setInteractive({ cursor: 'pointer' })
        this.closeButton.on('pointerup', () => this.close())
        this.closeButton.on('pointerover', () => {
            this.closeButton.setScale(0.75)
            this.closeX.setScale(0.75)
        })
        this.closeButton.on('pointerout', () => {
            this.closeButton.setScale(0.7)
            this.closeX.setScale(0.7)
        })

        // Quest indicator icon - positioned next to name
        this.questIcon = this.scene.add.text(-350, -175, '📜', {
            fontSize: '24px'
        })
        this.questIcon.setOrigin(0.5, 0.5)
        this.questIcon.visible = false
        this.add(this.questIcon)
    }

    /**
     * Show NPC dialog
     * @param {Object} data - Dialog data from server
     */
    show(data) {
        if (!data) return

        this.npcId = data.npcId
        this.npcName = data.name
        this.currentDialog = data.dialog
        this.activeQuests = data.activeQuests || []
        this.completedQuests = data.completedQuests || []
        this.quests = data.quests || []

        // Update name
        this.nameText.text = (this.npcName || 'NPC').toUpperCase()

        // Show quest icon if NPC has quests
        this.questIcon.visible = this.quests.length > 0

        // Display dialog
        if (this.currentDialog) {
            this.displayDialog(this.currentDialog)
        } else {
            this.dialogText.text = 'This NPC has nothing to say right now.'
            this.clearResponses()
            this.createOkButton()
        }

        // Hide HTML overlay z-index so this prompt appears on top
        this.interface?.prompt?.hideOverlayZIndex()

        super.show()
    }

    /**
     * Display a dialog node
     * @param {Object} dialog - Dialog node { id, text, responses }
     */
    displayDialog(dialog) {
        if (!dialog) {
            this.close()
            return
        }

        this.currentDialog = dialog

        // Set dialog text
        this.dialogText.text = dialog.text || ''

        // Clear and create response buttons
        this.clearResponses()

        if (dialog.responses && dialog.responses.length > 0) {
            this.createResponseButtons(dialog.responses)
        } else {
            // No responses - just show OK button
            this.createOkButton()
        }

        // Adjust dialog box height based on content
        this.adjustDialogSize()
    }

    /**
     * Create response buttons
     * @param {Array} responses - Array of response objects
     */
    createResponseButtons(responses) {
        const buttonSpacing = 85 // Better spacing between buttons
        const startY = 0 // Relative to container

        responses.forEach((response, index) => {
            const y = startY + (index * buttonSpacing)
            const button = this.createResponseButton(response, index, y)
            this.responsesContainer.add(button)
        })
    }

    /**
     * Create a single response button
     */
    createResponseButton(response, index, y) {
        const container = this.scene.add.container(0, y)

        // Button background using CP sprite - scaled down height
        const button = this.scene.add.image(0, 0, 'prompt', 'window-button')
        button.setScale(1, 0.8) // Smaller height
        container.add(button)

        // Button text
        const text = this.scene.add.text(0, 0, response.text, {
            fontFamily: 'Arial Narrow',
            fontSize: '20px',
            fontStyle: 'bold',
            color: '#ffffff',
            align: 'center',
            fixedWidth: 260,
            stroke: '#000000',
            strokeThickness: 2
        })
        text.setOrigin(0.5, 0.5)
        container.add(text)

        // Make interactive using Button component
        const btnComponent = new Button(button)
        btnComponent.spriteName = 'window-button'
        btnComponent.callback = () => this.onResponseClick(response, index)

        return container
    }

    /**
     * Create OK button for dialogs with no responses
     */
    createOkButton() {
        const container = this.scene.add.container(0, 0)

        const button = this.scene.add.image(0, 0, 'prompt', 'window-button')
        button.setScale(1, 0.8) // Smaller height
        container.add(button)

        const text = this.scene.add.text(0, 0, 'Ok', {
            fontFamily: 'Arial Narrow',
            fontSize: '24px',
            fontStyle: 'bold',
            color: '#ffffff',
            align: 'center',
            stroke: '#000000',
            strokeThickness: 3
        })
        text.setOrigin(0.5, 0.5)
        container.add(text)

        const btnComponent = new Button(button)
        btnComponent.spriteName = 'window-button'
        btnComponent.callback = () => this.close()

        this.responsesContainer.add(container)
    }

    /**
     * Handle response button click
     * @param {Object} response - Response object
     * @param {number} index - Response index
     */
    onResponseClick(response, index) {
        // Send response to server
        this.network.send('npc_dialog_response', {
            npcId: this.npcId,
            dialogId: this.currentDialog?.id,
            responseIndex: index
        })

        // Handle local actions for immediate feedback
        switch (response.action) {
            case 'close':
                this.close()
                break
                
            case 'next':
                // Server will send new dialog
                break
                
            case 'start_quest':
                // Show quest started feedback
                this.showQuestStarted(response.questId)
                break
                
            default:
                // Wait for server response
                break
        }
    }

    /**
     * Show quest started feedback
     * @param {string} questId - Quest ID
     */
    showQuestStarted(questId) {
        const quest = this.quests.find(q => q.id === questId)
        if (quest) {
            // Could show a toast notification here
            console.log(`[NPCDialog] Quest started: ${quest.name}`)
        }
    }

    /**
     * Clear all response buttons
     */
    clearResponses() {
        this.responsesContainer.removeAll(true)
    }

    /**
     * Adjust dialog box size based on content
     */
    adjustDialogSize() {
        // Dynamic resizing removed to maintain CP style fixed window
        // Could be re-added later with proper NineSlice updates if needed
    }

    /**
     * Update dialog with new data from server
     * @param {Object} data - New dialog data
     */
    updateDialog(data) {
        if (data.dialog) {
            this.displayDialog(data.dialog)
        }
    }

    /**
     * Close the dialog
     */
    close() {
        // Send close event to server
        if (this.npcId) {
            this.network.send('npc_dialog_close', { npcId: this.npcId })
        }

        this.npcId = null
        this.currentDialog = null
        this.clearResponses()
        
        // Restore HTML overlay z-index when closing
        this.interface?.prompt?.showOverlayZIndex()
        
        super.close()
    }

}
